import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { MapPin, Calendar, User, Clock, ChevronDown, ChevronRight, Trash2, Edit2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ClockHistoryTab = () => {
  const [clockEntries, setClockEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [groupedEntries, setGroupedEntries] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in_time: '', clock_out_time: '' });
  const [saving, setSaving] = useState(false);

  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    user_id: '', date: new Date().toISOString().split('T')[0],
    clock_in_time: '08:00', clock_out_time: '17:00', notes: ''
  });

  useEffect(() => { loadData(); }, []);
  useEffect(() => { filterEntries(); }, [selectedUser, startDate, endDate, clockEntries]);

  useEffect(() => {
    const grouped = {};
    filteredEntries.forEach(entry => {
      const key = `${entry.user_id}_${entry.date}`;
      if (!grouped[key]) {
        grouped[key] = { key, user_id: entry.user_id, user_name: entry.user_name, date: entry.date, entries: [], totalHours: 0 };
      }
      grouped[key].entries.push(entry);
      grouped[key].totalHours += entry.hours_worked || 0;
    });
    const arr = Object.values(grouped).map(g => ({ ...g, entries: g.entries.sort((a, b) => new Date(a.clock_in) - new Date(b.clock_in)) }));
    arr.sort((a, b) => new Date(b.date) - new Date(a.date));
    setGroupedEntries(arr);
    if (arr.length > 0 && Object.keys(expandedGroups).length === 0) setExpandedGroups({ [arr[0].key]: true });
  }, [filteredEntries]);

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  const expandAll = () => { const all = {}; groupedEntries.forEach(g => { all[g.key] = true; }); setExpandedGroups(all); };
  const collapseAll = () => setExpandedGroups({});

  const loadData = async () => {
    try {
      const [entriesRes, usersRes] = await Promise.all([
        axios.get('/clock/all'),
        axios.get('/clock/users')
      ]);
      setClockEntries(entriesRes.data || []);
      setFilteredEntries(entriesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      if (error.response?.status === 403) toast.error('Solo administradores pueden ver el historial');
      else toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    let filtered = [...clockEntries];
    if (selectedUser !== 'all') filtered = filtered.filter(e => e.user_id === selectedUser);
    filtered = filtered.filter(entry => {
      const d = new Date(entry.date);
      return d >= new Date(startDate) && d <= new Date(endDate + 'T23:59:59');
    });
    filtered.sort((a, b) => new Date(b.clock_in) - new Date(a.clock_in));
    setFilteredEntries(filtered);
  };

  const getTotalHours = () => filteredEntries.filter(e => e.hours_worked).reduce((s, e) => s + e.hours_worked, 0).toFixed(2);

  const handleDeleteClock = async (clockId, userName) => {
    if (!window.confirm(`¿Eliminar ponche de ${userName}?`)) return;
    try {
      await axios.delete(`/clock/${clockId}`);
      toast.success('Ponche eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleOpenEditDialog = (entry) => {
    setEditingEntry(entry);
    const ci = entry.clock_in ? new Date(entry.clock_in).toTimeString().slice(0, 5) : '';
    const co = entry.clock_out ? new Date(entry.clock_out).toTimeString().slice(0, 5) : '';
    setEditForm({ clock_in_time: ci, clock_out_time: co });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    setSaving(true);
    try {
      const date = editingEntry.date;
      const clockInFull = editForm.clock_in_time ? `${date}T${editForm.clock_in_time}:00` : null;
      const clockOutFull = editForm.clock_out_time ? `${date}T${editForm.clock_out_time}:00` : null;
      await axios.put(`/clock/${editingEntry.clock_id}`, { clock_in: clockInFull, clock_out: clockOutFull });
      toast.success('Ponche actualizado');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateManualEntry = async () => {
    if (!manualForm.user_id || !manualForm.date || !manualForm.clock_in_time) {
      toast.error('Complete los campos requeridos'); return;
    }
    setSaving(true);
    try {
      await axios.post('/clock/manual', manualForm);
      toast.success('Ponche manual creado');
      setManualDialogOpen(false);
      setManualForm({ user_id: '', date: new Date().toISOString().split('T')[0], clock_in_time: '08:00', clock_out_time: '17:00', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const formatLocalDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatClockTime = (isoStr) => {
    if (!isoStr) return '--:--';
    return new Date(isoStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const calcHours = (t1, t2) => {
    if (!t1 || !t2) return 'N/A';
    const [h1, m1] = t1.split(':').map(Number);
    const [h2, m2] = t2.split(':').map(Number);
    const diff = (h2 * 60 + m2 - h1 * 60 - m1) / 60;
    return diff > 0 ? `${diff.toFixed(2)} horas` : 'Hora inválida';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-12 h-12 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="clock-history-panel">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
          <h2 className="text-xl font-bold text-slate-800">Historial de Ponches</h2>
        </div>
        <Button onClick={() => setManualDialogOpen(true)} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="manual-punch-btn">
          <Plus className="w-4 h-4 mr-2" /> Ponche Manual
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Filtros</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Empleado</label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Fecha Inicio</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Fecha Fin</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-slate-600">
              <span className="font-semibold">{groupedEntries.length}</span> días |{' '}
              <span className="font-semibold">{filteredEntries.length}</span> ponches |{' '}
              Total: <span className="font-semibold text-blue-600">{getTotalHours()}h</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>Expandir</Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>Colapsar</Button>
              <Button onClick={loadData} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700">Actualizar</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grouped Entries */}
      <div className="space-y-3">
        {groupedEntries.length > 0 ? groupedEntries.map(group => (
          <Collapsible key={group.key} open={expandedGroups[group.key]} onOpenChange={() => toggleGroup(group.key)}>
            <Card className="overflow-hidden">
              <CollapsibleTrigger asChild>
                <div className="cursor-pointer hover:bg-slate-50 transition-colors">
                  <CardHeader className="pb-3 border-b bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8">
                          {expandedGroups[group.key] ? <ChevronDown className="w-5 h-5 text-slate-500" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-800">{group.user_name || 'Sin nombre'}</h3>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Calendar className="w-4 h-4" /> {formatLocalDate(group.date)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold font-mono text-blue-600">{group.totalHours.toFixed(2)}h</div>
                        <Badge className="bg-blue-100 text-blue-700">{group.entries.length} ponche{group.entries.length !== 1 ? 's' : ''}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {group.entries.map((entry, idx) => (
                      <div key={entry.clock_id} className={`p-4 rounded-lg border ${entry.status === 'active' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 text-sm font-medium flex items-center justify-center">{idx + 1}</span>
                            <Badge className={entry.status === 'active' ? 'bg-green-500' : 'bg-slate-500'}>{entry.status === 'active' ? 'Activo' : 'Completado'}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            {entry.hours_worked > 0 && <span className="font-mono font-semibold text-blue-600">{entry.hours_worked.toFixed(2)}h</span>}
                            <Button variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50 p-1 h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(entry); }}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 p-1 h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); handleDeleteClock(entry.clock_id, entry.user_name); }}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-green-600" /> <span className="font-medium">Entrada:</span>
                            <span className="font-mono">{formatClockTime(entry.clock_in)}</span>
                          </div>
                          {entry.clock_out && (
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-red-600" /> <span className="font-medium">Salida:</span>
                              <span className="font-mono">{formatClockTime(entry.clock_out)}</span>
                            </div>
                          )}
                        </div>
                        {entry.notes && entry.notes !== 'Ponche automático' && (
                          <div className="mt-2 text-sm text-slate-600 bg-white p-2 rounded border">
                            <span className="font-medium">Notas:</span> {entry.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )) : (
          <Card><CardContent className="p-12 text-center text-slate-500">
            <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No hay registros para los filtros seleccionados</p>
          </CardContent></Card>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit2 className="w-5 h-5" /> Editar Ponche</DialogTitle></DialogHeader>
          {editingEntry && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Empleado:</strong> {editingEntry.user_name}</p>
                <p><strong>Fecha:</strong> {formatLocalDate(editingEntry.date)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="w-4 h-4 text-green-600" /> Hora Entrada</Label>
                  <Input type="time" value={editForm.clock_in_time} onChange={(e) => setEditForm({...editForm, clock_in_time: e.target.value})} className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="w-4 h-4 text-red-600" /> Hora Salida</Label>
                  <Input type="time" value={editForm.clock_out_time} onChange={(e) => setEditForm({...editForm, clock_out_time: e.target.value})} className="font-mono" />
                </div>
              </div>
              {editForm.clock_in_time && editForm.clock_out_time && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                  <strong>Horas calculadas:</strong> {calcHours(editForm.clock_in_time, editForm.clock_out_time)}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Ponche Manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2"><User className="w-4 h-4" /> Empleado *</Label>
              <Select value={manualForm.user_id} onValueChange={(v) => setManualForm({...manualForm, user_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Fecha *</Label>
              <Input type="date" value={manualForm.date} onChange={(e) => setManualForm({...manualForm, date: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Clock className="w-4 h-4 text-green-600" /> Entrada *</Label>
                <Input type="time" value={manualForm.clock_in_time} onChange={(e) => setManualForm({...manualForm, clock_in_time: e.target.value})} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Clock className="w-4 h-4 text-red-600" /> Salida *</Label>
                <Input type="time" value={manualForm.clock_out_time} onChange={(e) => setManualForm({...manualForm, clock_out_time: e.target.value})} className="font-mono" />
              </div>
            </div>
            {manualForm.clock_in_time && manualForm.clock_out_time && (
              <div className="bg-green-50 p-3 rounded-lg text-sm text-green-700 border border-green-200">
                <strong>Horas:</strong> {calcHours(manualForm.clock_in_time, manualForm.clock_out_time)}
              </div>
            )}
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={manualForm.notes} onChange={(e) => setManualForm({...manualForm, notes: e.target.value})} placeholder="Ej: Olvidó ponchar..." rows={2} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateManualEntry} disabled={saving || !manualForm.user_id || !manualForm.date || !manualForm.clock_in_time}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">{saving ? 'Creando...' : 'Registrar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClockHistoryTab;
